import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, Volume2, VolumeX, Loader2 } from "lucide-react";
import { GULF_VIDEO_URL, GULF_VIDEO_LABEL } from "../lib/gulfConstants";
import { trackVideo, trackCtaClick } from "../lib/api";
import {
  trackClickButton,
  trackAddToCart,
  trackInitiateCheckout,
} from "../lib/tiktok";

// ─────────────────────────────────────────────────────────────────────────
// Thabit video modal — "أستمع إلى مقدمة عن ثابت"
//   • Brand fully reskinned (no "الكتاب الأزرق", no $).
//   • Adds a draggable progress slider so users can seek forward/back.
//   • CTA is a direct <a href> to Stripe Payment Link (preserves mobile
//     viewport behaviour — see LandingAI.jsx rationale).
//   • Fires `buy-thabit-video-modal` analytics so admin sees these clicks
//     in the Thabit Stripe Payment Link breakdown.
// ─────────────────────────────────────────────────────────────────────────

export default function ThabitVideoModal({
  open,
  onClose,
  stripeUrl,
  priceLabel = "199 ريال",
}) {
  const videoRef = useRef(null);
  const milestonesFired = useRef(new Set());
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      milestonesFired.current = new Set();
      setCurrentTime(0);
      setPlaying(false);
    } else {
      document.body.style.overflow = "";
      const v = videoRef.current;
      if (v) { try { v.pause(); } catch (_) { /* ignore */ } }
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // ESC closes; arrow keys are intentionally allowed here for seeking.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const handlePlayPause = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    setCurrentTime(v.currentTime);
    const pct = (v.currentTime / v.duration) * 100;
    [25, 50, 75].forEach((m) => {
      if (pct >= m && !milestonesFired.current.has(m)) {
        milestonesFired.current.add(m);
        trackVideo("progress", m, GULF_VIDEO_LABEL);
      }
    });
  };

  const handlePlay = () => {
    setPlaying(true);
    if (!milestonesFired.current.has("play")) {
      milestonesFired.current.add("play");
      trackVideo("play", 0, GULF_VIDEO_LABEL);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    if (!milestonesFired.current.has("complete")) {
      milestonesFired.current.add("complete");
      trackVideo("complete", 100, GULF_VIDEO_LABEL);
    }
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const target = Number(e.target.value);
    v.currentTime = target;
    setCurrentTime(target);
  };

  const handleBuy = () => {
    try {
      trackClickButton("thabit-video-modal");
      trackCtaClick("buy-thabit-video-modal", "landing-gulf");
      trackAddToCart("thabit_audio");
      trackInitiateCheckout("thabit_audio");
    } catch (_) { /* never block navigation */ }
  };

  const formatTime = (sec) => {
    if (!isFinite(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  if (!open) return null;

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6"
      data-testid="thabit-video-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-[#040914]/95 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[95vh] overflow-y-auto bg-[#0B1D3A] border border-[#C5A059]/40 rounded-sm shadow-2xl shadow-black/70 fade-up">
        <button
          onClick={onClose}
          className="absolute top-3 left-3 z-10 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/5 transition"
          aria-label="إغلاق"
          data-testid="thabit-video-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 sm:px-8 pt-9 pb-3 text-center">
          <div className="inline-block text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-2">مقدمة من ثابت</div>
          <h3 className="font-display text-lg sm:text-2xl font-black text-white leading-snug">
            أستمع إلى <span className="text-[#C5A059]">مقدمة عن ثابت</span>
          </h3>
        </div>

        <div className="px-3 sm:px-8 py-3">
          <div
            className="relative w-full bg-black rounded-sm overflow-hidden select-none"
            style={{ aspectRatio: "16 / 9" }}
            data-testid="thabit-video-frame"
            onContextMenu={(e) => e.preventDefault()}
          >
            <video
              ref={videoRef}
              src={GULF_VIDEO_URL}
              playsInline
              preload="auto"
              controls={false}
              controlsList="nodownload noremoteplayback noplaybackrate"
              disablePictureInPicture
              disableRemotePlayback
              className="w-full h-full"
              onClick={handlePlayPause}
              onPlay={handlePlay}
              onPause={() => setPlaying(false)}
              onEnded={handleEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={(e) => { setLoading(false); setDuration(e.target.duration || 0); }}
              onCanPlay={() => setLoading(false)}
              onWaiting={() => setLoading(true)}
              onPlaying={() => setLoading(false)}
            />
            {!playing && !loading && (
              <button
                type="button"
                onClick={handlePlayPause}
                aria-label="تشغيل"
                data-testid="thabit-video-play-overlay"
                className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors"
              >
                <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center shadow-[0_0_40px_rgba(197,160,89,0.6)]">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 mr-1" fill="currentColor" />
                </span>
              </button>
            )}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
              </div>
            )}
          </div>

          {/* Custom progress slider — users CAN seek forward/back. */}
          <div className="mt-3 px-1" dir="ltr">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step="0.1"
              value={currentTime}
              onChange={handleSeek}
              data-testid="thabit-video-scrubber"
              aria-label="الانتقال في الفيديو"
              className="thabit-scrubber w-full h-1.5 appearance-none cursor-pointer bg-white/15 rounded-full outline-none"
              style={{
                background: `linear-gradient(to right, #C5A059 0%, #C5A059 ${progressPct}%, rgba(255,255,255,0.15) ${progressPct}%, rgba(255,255,255,0.15) 100%)`,
              }}
            />
            <div className="mt-1.5 flex items-center justify-between text-[11px] text-white/60 font-mono">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3" dir="ltr">
            <button
              type="button"
              onClick={handlePlayPause}
              data-testid="thabit-video-playpause"
              className="flex items-center gap-2 bg-[#C5A059]/15 hover:bg-[#C5A059]/25 border border-[#C5A059]/40 text-[#C5A059] px-4 py-2 rounded-sm text-sm font-bold transition-colors"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{playing ? "إيقاف مؤقت" : "تشغيل"}</span>
            </button>
            <button
              type="button"
              onClick={() => {
                const v = videoRef.current; if (!v) return;
                v.muted = !v.muted; setMuted(v.muted);
              }}
              data-testid="thabit-video-mute"
              className="flex items-center gap-2 text-white/80 hover:text-white px-3 py-2 rounded-sm text-sm transition-colors"
              aria-label={muted ? "تشغيل الصوت" : "كتم الصوت"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="px-5 sm:px-8 pb-7 pt-2" dir="rtl" data-testid="thabit-video-modal-cta-block">
          <p className="text-center text-sm sm:text-base text-white/90 leading-relaxed mb-4">
            أعجبك ما سمعت؟ احصل على <span className="text-[#C5A059] font-bold">ثابت كاملاً</span> الآن.
          </p>
          <a
            href={stripeUrl}
            onClick={handleBuy}
            data-testid="thabit-video-modal-buy-btn"
            className="w-full inline-flex items-center justify-center bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-black text-base sm:text-lg py-5 rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse transition-colors"
          >
            <span className="flex flex-col items-center gap-0.5 leading-tight">
              <span>احصل على ثابت الآن · {priceLabel}</span>
              <span className="text-[12px] sm:text-sm font-semibold text-[#040914]/80">استلام فوري · ضمان 7 أيام</span>
            </span>
          </a>
          <p className="mt-3 text-center text-[11px] text-[#A1B0C6]">دفع آمن ومشفّر · جلسات ثابت الصوتية كاملة</p>
        </div>
      </div>

      {/* Scrubber thumb styling (input[type=range]) */}
      <style>{`
        .thabit-scrubber::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px; height: 16px;
          background: #C5A059;
          border-radius: 50%;
          border: 2px solid #040914;
          box-shadow: 0 0 6px rgba(197,160,89,0.6);
          cursor: pointer;
        }
        .thabit-scrubber::-moz-range-thumb {
          width: 16px; height: 16px;
          background: #C5A059;
          border-radius: 50%;
          border: 2px solid #040914;
          box-shadow: 0 0 6px rgba(197,160,89,0.6);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
